<script lang="ts" setup>
import type { SizeVariants } from '~kui/types/kui';

const props = withDefaults(defineProps<Props>(), {
  active: false,
  size: 'md',
  variant: 'default',
});

const emit = defineEmits<{
  click: [e: MouseEvent];
}>();

interface Props {
  active?: boolean;
  size?: Extract<SizeVariants, 'sm' | 'md' | 'lg'>;
  variant?: 'default' | 'pill';
}

const pillSizes = {
  sm: { inactive: 'w-1.5 h-1.5', active: 'w-6 h-1.5' },
  md: { inactive: 'w-2 h-2', active: 'w-8 h-2' },
  lg: { inactive: 'w-3 h-3', active: 'w-10 h-3' },
};

const pillClasses = computed(() => {
  const size = pillSizes[props.size];
  return [
    props.active ? size.active : size.inactive,
    props.active ? 'bg-secondary' : 'bg-input hover:bg-muted-foreground',
  ];
});

const defaultClassList = computed(() => {
  return twMerge(
    cva('relative cursor-pointer rounded-full transition-all duration-300 motion-reduce:transition-none before:absolute before:inset-[-8px] before:content-[\'\'] focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2', {
      variants: {
        active: {
          true: 'bg-primary',
          false: 'bg-input hover:bg-muted-foreground',
        },
        size: {
          sm: 'size-2',
          md: 'size-3',
          lg: 'size-4',
        },
      },
    })({
      active: props.active,
      size: props.size,
    }),
  );
});

const onClick = (event: MouseEvent) => {
  emit('click', event);
};
</script>

<template>
  <!-- Pill variant: CSS transitions -->
  <button
    v-if="variant === 'pill'"
    class="relative cursor-pointer rounded-full border border-border transition-[width] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none before:absolute before:inset-[-8px] before:content-[''] focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2"
    :class="pillClasses"
    type="button"
    :aria-current="active ? 'true' : undefined"
    @click="onClick"
  />

  <!-- Default variant: CSS transitions -->
  <button
    v-else
    :class="defaultClassList"
    type="button"
    :aria-current="active ? 'true' : undefined"
    @click="onClick"
  />
</template>
